import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  driveClient,
  DriveEntry,
  DriveTreeNode,
  DriveTrashEntry,
} from '@/lib/drive/driveClient';
import { useWebSocket } from '@/contexts/WebSocketContext';
import { appConfirm, appPrompt } from '@/lib/dialogs';
import type { UploadTask } from '@/components/drive/UploadProgressPanel';

export type DriveView = 'files' | 'trash';
export type DriveViewMode = 'grid' | 'list';
export type DriveSortBy = 'name' | 'size' | 'date';

interface ClipboardItem {
  path: string;
  name: string;
  kind: 'file' | 'folder';
  op: 'cut' | 'copy';
}

const ROOT = '/';
/** Above this size an upload goes through the resumable chunked session instead of one body. */
const RESUMABLE_THRESHOLD = 32 * 1024 * 1024;
const UPLOAD_CONCURRENCY = 3;

/** Build the folder tree from the client's flat rows — derived, never stored. */
function buildTree(rows: { id: string; name: string; parentId: string | null }[]): DriveTreeNode[] {
  const byParent = new Map<string | null, { id: string; name: string; parentId: string | null }[]>();
  rows.forEach((row) => {
    const list = byParent.get(row.parentId ?? null) ?? [];
    list.push(row);
    byParent.set(row.parentId ?? null, list);
  });
  const walk = (parentId: string | null, parentPath: string): DriveTreeNode[] =>
    (byParent.get(parentId) ?? []).map((row) => {
      const path = parentPath === ROOT ? `/${row.name}` : `${parentPath}/${row.name}`;
      return { id: row.id, name: row.name, path, children: walk(row.id, path) };
    });
  return walk(null, ROOT);
}

/**
 * State owner for the drive surface: the open folder's entries, the trash
 * view, the clipboard and every mutation. The folder tree and the sorted
 * listings derive on render — nothing here mirrors them into extra state.
 */
export function useDrive(view: DriveView) {
  const { t, i18n } = useTranslation();
  const { socket } = useWebSocket();

  const [currentPath, setCurrentPath] = useState(ROOT);
  const [entries, setEntries] = useState<DriveEntry[]>([]);
  const [trash, setTrash] = useState<DriveTrashEntry[]>([]);
  const [folderRows, setFolderRows] = useState<DriveClientRows>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [clipboard, setClipboard] = useState<ClipboardItem | null>(null);
  const [viewMode, setViewMode] = useState<DriveViewMode>('grid');
  const [sortBy, setSortBy] = useState<DriveSortBy>('date');
  const [sortAscending, setSortAscending] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);

  /* ---- multi-selection, search, dialogs, upload queue ---- */
  const [selectedPaths, setSelectedPaths] = useState<Set<string>>(new Set());
  const anchorPathRef = useRef<string | null>(null);
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number } | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<DriveEntry[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [uploadTasks, setUploadTasks] = useState<UploadTask[]>([]);
  const abortsRef = useRef(new Map<string, AbortController>());
  const [previewEntry, setPreviewEntry] = useState<DriveEntry | null>(null);
  const [shareEntry, setShareEntry] = useState<DriveEntry | null>(null);
  const [versionsEntry, setVersionsEntry] = useState<DriveEntry | null>(null);

  const refresh = useCallback(async () => {
    try {
      await driveClient.listFolder(currentPath, true);
      setEntries(await driveClient.listFolder(currentPath));
      setFolderRows(driveClient.getFolderRows());
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('errors.loadDrive'));
    } finally {
      setLoading(false);
    }
  }, [currentPath, t]);

  const refreshTrash = useCallback(async () => {
    try {
      setTrash(await driveClient.listTrash());
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('errors.loadTrash'));
    }
  }, [t]);

  useEffect(() => {
    if (view === 'trash') void refreshTrash();
    else void refresh();
  }, [view, refresh, refreshTrash]);

  // Live refresh: the hub's socket carries the drive's mutations (uploads
  // from other surfaces and devices included) — mirror them like the drive
  // app's own clients do.
  useEffect(() => {
    if (!socket) return;
    const onChange = () => {
      if (view === 'trash') void refreshTrash();
      else void refresh();
    };
    socket.on('fileUploaded', onChange);
    socket.on('fileDeleted', onChange);
    socket.on('fileUpdated', onChange);
    return () => {
      socket.off('fileUploaded', onChange);
      socket.off('fileDeleted', onChange);
      socket.off('fileUpdated', onChange);
    };
  }, [socket, view, refresh, refreshTrash]);

  const tree: DriveTreeNode[] = useMemo(() => buildTree(folderRows), [folderRows]);

  const sortedEntries: DriveEntry[] = useMemo(() => {
    const factor = sortAscending ? 1 : -1;
    return [...entries].sort((a, b) => {
      if (a.kind !== b.kind) return a.kind === 'folder' ? -1 : 1;
      if (sortBy === 'name') return factor * a.name.localeCompare(b.name, i18n.resolvedLanguage);
      if (sortBy === 'size') return factor * (a.size - b.size);
      return factor * ((a.updatedAt || a.createdAt) - (b.updatedAt || b.createdAt));
    });
  }, [entries, sortBy, sortAscending, i18n.resolvedLanguage]);

  /** Trash rows carry no paths — selection there keys on the row id. */
  const selectedTrash = useMemo(
    () => trash.find((row) => row.id === selectedKey) ?? null,
    [trash, selectedKey],
  );
  const selectedEntry = useMemo(
    () => (view === 'trash' ? null : entries.find((entry) => entry.path === selectedKey) ?? null),
    [entries, selectedKey, view],
  );

  const openFolder = useCallback((path: string) => {
    setCurrentPath(normalize(path));
    setSelectedKey(null);
    setSelectedPaths(new Set());
    anchorPathRef.current = null;
    setSearchResults(null);
    setSearchQuery('');
  }, []);

  /* --------------------------------------------------------- selection --- */

  /** Click semantics: plain click selects one, Ctrl/Cmd toggles, Shift ranges. */
  const selectEntry = useCallback(
    (entry: DriveEntry, modifiers: { multi?: boolean; range?: boolean } = {}) => {
      if (modifiers.range && anchorPathRef.current) {
        const from = sortedEntries.findIndex((candidate) => candidate.path === anchorPathRef.current);
        const to = sortedEntries.findIndex((candidate) => candidate.path === entry.path);
        if (from >= 0 && to >= 0) {
          const [start, end] = from <= to ? [from, to] : [to, from];
          const next = new Set<string>();
          for (let index = start; index <= end; index += 1) next.add(sortedEntries[index].path);
          setSelectedPaths(next);
          setSelectedKey(entry.path);
          return;
        }
      }
      if (modifiers.multi) {
        const next = new Set(selectedPaths);
        if (next.has(entry.path)) next.delete(entry.path);
        else next.add(entry.path);
        setSelectedPaths(next);
        setSelectedKey(entry.path);
        anchorPathRef.current = entry.path;
        return;
      }
      setSelectedPaths(new Set([entry.path]));
      setSelectedKey(entry.path);
      anchorPathRef.current = entry.path;
    },
    [selectedPaths, sortedEntries],
  );

  const selectAll = useCallback(() => {
    setSelectedPaths(new Set(sortedEntries.map((entry) => entry.path)));
  }, [sortedEntries]);

  const clearSelection = useCallback(() => {
    setSelectedPaths(new Set());
    setSelectedKey(null);
  }, []);

  const patchTask = useCallback((id: string, patch: Partial<UploadTask>) => {
    setUploadTasks((previous) => previous.map((task) => (task.id === id ? { ...task, ...patch } : task)));
  }, []);

  /** One upload task: small files ride one request with progress, large ones the
   *  resumable chunked session — the queue never blocks the listing either way. */
  const runUploadTask = useCallback(
    async (task: UploadTask, file: File, parentPath: string) => {
      const abort = new AbortController();
      abortsRef.current.set(task.id, abort);
      patchTask(task.id, { status: 'uploading' });
      const onProgress = (sent: number, total: number) => patchTask(task.id, { sent, size: total || file.size });
      try {
        if (file.size >= RESUMABLE_THRESHOLD) {
          await driveClient.uploadResumable(file, parentPath, onProgress, abort.signal);
        } else {
          await driveClient.uploadWithProgress(file, parentPath, onProgress, abort.signal);
        }
        patchTask(task.id, { status: 'done', sent: file.size });
      } catch (err) {
        const aborted = err instanceof DOMException && err.name === 'AbortError';
        patchTask(task.id, {
          status: aborted ? 'canceled' : 'error',
          error: aborted ? undefined : err instanceof Error ? err.message : String(err),
        });
      } finally {
        abortsRef.current.delete(task.id);
      }
    },
    [patchTask],
  );

  /** Queue the files at the current folder (or an explicit target for folder uploads). */
  const queueUploads = useCallback(
    async (files: File[], targetPath?: string, relativeRoots?: Map<File, string>) => {
      if (!files.length) return;
      const destination = targetPath ?? currentPath;
      const tasks: UploadTask[] = files.map((file) => ({
        id: crypto.randomUUID(),
        name: relativeRoots?.get(file) ? `${relativeRoots.get(file)}/${file.name}` : file.name,
        size: file.size,
        sent: 0,
        status: 'waiting',
      }));
      setUploadTasks((previous) => [...previous, ...tasks]);
      setUploading(true);

      // Folder uploads need their subtree first, so folders are created up front.
      const parentByTask = new Map<string, string>();
      if (relativeRoots) {
        for (const file of files) {
          const relative = relativeRoots.get(file);
          parentByTask.set(
            tasks.find((task) => task.name === (relative ? `${relative}/${file.name}` : file.name))!.id,
            relative ? await driveClient.ensureFolder(`${destination === ROOT ? '' : destination}/${relative}`) ?? destination : destination,
          );
        }
      } else {
        for (const task of tasks) parentByTask.set(task.id, destination);
      }

      let cursor = 0;
      const worker = async () => {
        while (cursor < tasks.length) {
          const task = tasks[cursor];
          cursor += 1;
          const file = files[tasks.indexOf(task)];
          await runUploadTask(task, file, parentByTask.get(task.id) ?? destination);
        }
      };
      await Promise.all(Array.from({ length: Math.min(UPLOAD_CONCURRENCY, tasks.length) }, worker));
      setUploading(false);
      await refresh();
      await refreshTrash();
    },
    [currentPath, refresh, refreshTrash, runUploadTask],
  );

  const uploadFiles = useCallback(
    async (files: File[]) => {
      await queueUploads(files);
      toast.success(
        files.length > 1
          ? t('drive.filesUploaded', { count: files.length })
          : t('drive.uploaded'),
      );
    },
    [queueUploads, t],
  );

  /** A drag-and-dropped directory walk (webkitGetAsEntry) or the directory input's
   *  webkitRelativePath — both flatten into the same file + relative-folder queue. */
  const uploadFolderFiles = useCallback(
    async (files: File[]) => {
      const withRelative = files.map((file) => {
        const relative = ((file as File & { webkitRelativePath?: string }).webkitRelativePath ?? '')
          .split('/')
          .slice(0, -1)
          .join('/');
        return { file, relative };
      });
      const roots = new Map<File, string>();
      withRelative.forEach(({ file, relative }) => roots.set(file, relative));
      await queueUploads(
        withRelative.map(({ file }) => file),
        undefined,
        roots,
      );
    },
    [queueUploads],
  );

  const cancelUpload = useCallback((id: string) => {
    abortsRef.current.get(id)?.abort();
  }, []);

  const clearUploads = useCallback(() => {
    setUploadTasks((previous) => previous.filter((task) => task.status === 'uploading' || task.status === 'waiting'));
  }, []);

  /* ------------------------------------------------------------ search --- */

  const runSearch = useCallback(
    async (query: string) => {
      const term = query.trim();
      setSearchQuery(query);
      if (!term) {
        setSearchResults(null);
        setSearching(false);
        return;
      }
      setSearching(true);
      try {
        setSearchResults(await driveClient.search(term));
      } catch (err) {
        toast.error(err instanceof Error ? err.message : t('errors.actionFailed'));
        setSearchResults(null);
      } finally {
        setSearching(false);
      }
    },
    [t],
  );

  const clearSearch = useCallback(() => {
    setSearchQuery('');
    setSearchResults(null);
  }, []);

  const runAction = useCallback(
    async (
      action: string,
      entry?: DriveEntry | DriveTrashEntry | null,
      extra?: { folderPath?: string },
    ) => {
      const target = entry ?? selectedEntry ?? selectedTrash;
      try {
        switch (action) {
          case 'open': {
            if (target && 'path' in target && target.kind === 'folder') openFolder(target.path);
            return;
          }
          case 'delete': {
            // Trash rows have no delete of their own — restore is the trash action.
            const targets = view === 'files' ? [...selectedPaths] : [];
            if (targets.length > 1) {
              const folders = entries.filter((entry) => targets.includes(entry.path) && entry.kind === 'folder');
              if (folders.length > 0) {
                const confirmed = await appConfirm(
                  t('drive.bulkFolderDeleteWarning', { count: folders.length }),
                  {
                    title: t('drive.delete'),
                    confirmLabel: t('drive.delete'),
                    cancelLabel: t('common.cancel'),
                    variant: 'error',
                  },
                );
                if (!confirmed) return;
              }
              await driveClient.bulk('delete', targets.map((path) => entries.find((entry) => entry.path === path)!.id));
              toast.success(t('drive.bulkMovedToTrash', { count: targets.length }));
              clearSelection();
              await refresh();
              await refreshTrash();
              return;
            }
            if (!target || !('path' in target)) return;
            if (target.kind === 'folder') {
              // Folders are removed permanently — confirm through the app's
              // own dialog, never the browser's.
              const confirmed = await appConfirm(
                t('drive.folderDeleteWarning', { name: target.name }),
                {
                  title: t('drive.delete'),
                  confirmLabel: t('drive.delete'),
                  cancelLabel: t('common.cancel'),
                  variant: 'error',
                },
              );
              if (!confirmed) return;
            }
            await driveClient.delete(target.path);
            if (target.kind === 'folder') {
              // The drive removes folder rows for good — say so honestly.
              toast.success(t('drive.folderDeleted', { name: target.name }));
            } else {
              toast.success(t('drive.movedToTrash', { name: target.name }));
            }
            setSelectedKey(null);
            await refresh();
            await refreshTrash();
            return;
          }
          case 'restore': {
            if (!target) return;
            await driveClient.restore(target.id);
            toast.success(t('drive.restored', { name: target.name }));
            setSelectedKey(null);
            await refreshTrash();
            await refresh();
            return;
          }
          case 'rename': {
            if (!target || view === 'trash') return;
            const name = await appPrompt(t('drive.newName'), target.name, {
              title: t('drive.rename'),
              confirmLabel: t('drive.rename'),
              cancelLabel: t('common.cancel'),
            });
            if (!name || name === target.name) return;
            await driveClient.rename((target as DriveEntry).path, name);
            await refresh();
            return;
          }
          case 'download': {
            if (target && 'path' in target && target.kind === 'file') {
              await driveClient.download(target.path);
            }
            return;
          }
          case 'share': {
            if (target && 'path' in target && target.kind === 'file') {
              setShareEntry(target);
            }
            return;
          }
          case 'preview': {
            if (target && 'path' in target && target.kind === 'file') {
              setPreviewEntry(target);
            }
            return;
          }
          case 'versions': {
            if (target && 'path' in target && target.kind === 'file') {
              setVersionsEntry(target);
            }
            return;
          }
          case 'emptyTrash': {
            const confirmed = await appConfirm(t('drive.emptyTrashWarning'), {
              title: t('drive.emptyTrash'),
              confirmLabel: t('drive.emptyTrash'),
              cancelLabel: t('common.cancel'),
              variant: 'error',
            });
            if (!confirmed) return;
            const result = await driveClient.emptyTrash();
            toast.success(t('drive.trashEmptied', { count: result.purged }));
            setSelectedKey(null);
            await refreshTrash();
            await refresh();
            return;
          }
          case 'permanent': {
            if (!target || !('id' in target)) return;
            const confirmed = await appConfirm(t('drive.permanentWarning', { name: target.name }), {
              title: t('drive.permanentDelete'),
              confirmLabel: t('drive.permanentDelete'),
              cancelLabel: t('common.cancel'),
              variant: 'error',
            });
            if (!confirmed) return;
            const result = await driveClient.permanentDelete(target.id);
            toast.success(t('drive.permanentDeleted', { count: result.purged }));
            setSelectedKey(null);
            await refreshTrash();
            return;
          }
          case 'moveTo': {
            // Drag-and-drop move: the dragged entry lands in the dropped-on folder.
            if (!target || !('path' in target) || !extra?.folderPath) return;
            await driveClient.move(target.path, extra.folderPath);
            toast.success(t('drive.moved', { name: target.name }));
            await refresh();
            return;
          }
          case 'cut':
          case 'copy': {
            if (!target || !('path' in target)) {
              toast.error(t('drive.selectFirst'));
              return;
            }
            setClipboard({
              path: target.path,
              name: target.name,
              kind: target.kind,
              op: action,
            });
            return;
          }
          case 'paste': {
            if (!clipboard) return;
            if (clipboard.op === 'cut') {
              await driveClient.move(clipboard.path, currentPath);
              setClipboard(null);
            } else {
              // Server-side duplicate: one request for a file, the whole tree for a folder.
              await driveClient.copyEntry(clipboard.path, currentPath);
              toast.success(t('drive.copied', { name: clipboard.name }));
            }
            await refresh();
            return;
          }
          case 'newFolder': {
            const name = await appPrompt(t('drive.folderName'), '', {
              title: t('drive.newFolder'),
              confirmLabel: t('common.save'),
              cancelLabel: t('common.cancel'),
            });
            if (!name) return;
            await driveClient.createFolder(currentPath, name);
            await refresh();
            return;
          }
          default:
            return;
        }
      } catch (err) {
        toast.error(err instanceof Error ? err.message : t('errors.actionFailed'));
      }
    },
    [
      clipboard,
      currentPath,
      openFolder,
      refresh,
      refreshTrash,
      selectedEntry,
      selectedTrash,
      t,
      view,
    ],
  );

  return {
    currentPath,
    openFolder,
    entries: sortedEntries,
    trash,
    tree,
    loading,
    uploading,
    uploadFiles,
    uploadFolderFiles,
    uploadTasks,
    cancelUpload,
    clearUploads,
    viewMode,
    setViewMode,
    sortBy,
    setSortBy,
    sortAscending,
    setSortAscending,
    selectedKey,
    setSelectedKey,
    selectedEntry,
    selectedTrash,
    selectedPaths,
    selectEntry,
    selectAll,
    clearSelection,
    contextMenu,
    setContextMenu,
    searchQuery,
    searchResults,
    searching,
    runSearch,
    clearSearch,
    previewEntry,
    setPreviewEntry,
    shareEntry,
    setShareEntry,
    versionsEntry,
    setVersionsEntry,
    detailsOpen,
    setDetailsOpen,
    clipboard,
    refresh,
    refreshTrash,
    runAction,
  };
}

type DriveClientRows = ReturnType<typeof driveClient.getFolderRows>;

function normalize(path: string): string {
  const trimmed = path.trim().replace(/\\/g, '/');
  if (!trimmed || trimmed === '/') return ROOT;
  const withSlash = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
  return withSlash.length > 1 ? withSlash.replace(/\/+$/, '') : ROOT;
}
